import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { DownloadCsv } from '@/components/admin/download-csv';
import { Callout, Field, buttonClass, inputClass } from '@/components/ui';
import { isRetired } from '@/lib/style-options';

/**
 * A stock item is one colour and one size of one product. Spec 011.
 *
 * Guy, 29 October 2026: "No free-text entry should be permitted for colour or
 * size at the stock-item level." So every colour and size on this file comes
 * from a product's own lists, as a choice, never as a typed word:
 *
 *   NewStockItem    pick a product, then its colour and size (FR-1120)
 *   ChangeProduct   move one stock item to another product (FR-1121)
 *   MatchingPanel   the spreadsheet for all 3,338 of them (FR-1130 to FR-1136)
 *
 * The rules are the server's and the database's. These forms only offer what
 * those will accept, and show the refusal in the server's words when not.
 */

type Product = {
  id: string;
  name: string;
  active: boolean;
  colors: { name: string; hex: string; retired?: boolean }[] | null;
  sizes: string[] | null;
  retired_sizes?: string[] | null;
  product_variants: { colour: string; size: string; active: boolean }[];
};

async function post(path: string, body: unknown) {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => null)) as
    | { error?: { message?: string; details?: Record<string, unknown> } } & Record<string, unknown> | null;
  if (!res.ok) {
    const e = new Error(json?.error?.message ?? `Request failed (${res.status})`) as Error & { details?: Record<string, unknown> };
    e.details = json?.error?.details;
    throw e;
  }
  return json;
}

/** The list's retired mark (spec 014, FR-1450). The same rule as the server. */
const retired = (p: Product, kind: 'colour' | 'size', name: string) => isRetired(p, kind, name);

/** Search the products by name and choose one. */
export function ProductPicker({ id, value, onPick }: { id: string; value: Product | null; onPick: (p: Product | null) => void }) {
  const [q, setQ] = useState(value?.name ?? '');
  const needle = q.trim();
  const found = useQuery({
    queryKey: ['admin', 'product-pick', needle],
    enabled: needle.length >= 2 && needle !== value?.name,
    queryFn: async () => {
      const res = await fetch(`/api/products?q=${encodeURIComponent(needle)}&limit=20`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Could not search the products.');
      return ((await res.json()) as { rows: Product[] }).rows;
    },
  });

  return (
    <div>
      <input
        id={id}
        className={inputClass}
        value={q}
        placeholder="Type two letters of the product name"
        autoComplete="off"
        onChange={(e) => { setQ(e.target.value); if (value) onPick(null); }}
      />
      {needle.length >= 2 && needle !== value?.name && (
        <ul className="mt-1 max-h-[220px] overflow-y-auto rounded-[8px] border border-line bg-panel text-[13px]">
          {found.isLoading && <li className="px-3 py-2 text-muted">Searching</li>}
          {found.data?.length === 0 && <li className="px-3 py-2 text-muted">No product has that in its name.</li>}
          {found.data?.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-[color:var(--color-inset)]"
                onClick={() => { onPick(p); setQ(p.name); }}
              >
                {p.name}
                {!p.active && <span className="text-muted"> (inactive)</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The colour and size choices of one product, retired ones left out. */
function OptionChoice(
  { product, colour, size, setColour, setSize, idPrefix }:
  {
    product: Product; colour: string; size: string; idPrefix: string;
    setColour: (c: string) => void; setSize: (s: string) => void;
  },
) {
  const colours = (product.colors ?? []).filter((c) => !retired(product, 'colour', c.name));
  const sizes = (product.sizes ?? []).filter((s) => !retired(product, 'size', s));
  return (
    <>
      <Field label="Colour" htmlFor={`${idPrefix}-colour`}>
        <select id={`${idPrefix}-colour`} className={inputClass} value={colour} onChange={(e) => setColour(e.target.value)}>
          <option value="">Choose a colour</option>
          {colours.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Size" htmlFor={`${idPrefix}-size`}>
        <select id={`${idPrefix}-size`} className={inputClass} value={size} onChange={(e) => setSize(e.target.value)}>
          <option value="">Choose a size</option>
          {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Field>
      <p className="text-[12px] text-muted sm:col-span-2">
        The colours and sizes are the product's own. To use another one, add it on the product's page first.
      </p>
    </>
  );
}

/** FR-1120. Opens the new item, or the one that already holds that place. */
export function NewStockItem({ onOpen, onClose }: { onOpen: (itemId: string) => void; onClose: () => void }) {
  const qc = useQueryClient();
  const [product, setProduct] = useState<Product | null>(null);
  const [colour, setColour] = useState('');
  const [size, setSize] = useState('');
  const [existing, setExisting] = useState<string | null>(null);
  const make = useMutation({
    mutationFn: () => post(`/api/styles/${product!.id}/stock-items`, { colour, size }),
    onSuccess: (json) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      onOpen((json as { item: { id: string } }).item.id);
    },
    onError: (e: Error & { details?: Record<string, unknown> }) => {
      setExisting(typeof e.details?.item_id === 'string' ? e.details.item_id : null);
    },
  });

  return (
    <form
      aria-label="New stock item"
      className="mb-4 rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); make.mutate(); }}
    >
      <p className="mb-3 text-[15px] font-semibold">New stock item</p>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Product" htmlFor="new-item-product">
            <ProductPicker
              id="new-item-product"
              value={product}
              onPick={(p) => { setProduct(p); setColour(''); setSize(''); make.reset(); }}
            />
          </Field>
        </div>
        {product && (
          <OptionChoice
            product={product} colour={colour} size={size} idPrefix="new-item"
            setColour={(c) => { setColour(c); make.reset(); }}
            setSize={(s) => { setSize(s); make.reset(); }}
          />
        )}
      </div>
      {make.error && (
        <Callout tone={existing ? 'neutral' : 'danger'} title={make.error.message} className="mt-3">
          {existing && (
            <button type="button" className="underline" onClick={() => onOpen(existing)}>
              Open it
            </button>
          )}
        </Callout>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="submit"
          className={buttonClass('primary', 'sm')}
          disabled={!product || !colour || !size || make.isPending}
        >
          {make.isPending ? 'Making it' : 'Make the stock item'}
        </button>
        <button type="button" className={buttonClass('secondary', 'sm')} onClick={onClose}>Cancel</button>
      </div>
      <p className="mt-3 text-[12px] text-muted">
        The item opens next. Set its supplier and cost there, and record its stock with Adjust or a receipt.
      </p>
    </form>
  );
}

/** FR-1121. The stock item keeps its code, stock and history. */
export function ChangeProduct(
  { itemId, current }: { itemId: string; current: { product_id: string; colour: string; size: string } },
) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [colour, setColour] = useState('');
  const [size, setSize] = useState('');
  const [warning, setWarning] = useState<string | null>(null);
  const move = useMutation({
    mutationFn: () => post(`/api/items/${itemId}/product`, { product_id: product!.id, colour, size }),
    onSuccess: (json) => {
      setWarning((json as { warning?: string | null }).warning ?? null);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['stock-item', itemId] });
      void qc.invalidateQueries({ queryKey: ['admin', 'style'] });
    },
  });

  if (!open) {
    return (
      <>
        <button
          type="button"
          onClick={() => { setOpen(true); setWarning(null); }}
          className="rounded-[10px] border border-line px-3 py-2 text-[13px] font-semibold"
        >
          Change product
        </button>
        {warning && <Callout tone="warn" title={warning} className="w-full" />}
      </>
    );
  }
  const same = product?.id === current.product_id && colour === current.colour && size === current.size;
  return (
    <form
      aria-label="Change product"
      className="w-full rounded-[10px] border border-line bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); move.mutate(); }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Field label="Move to the product" htmlFor="move-product">
            <ProductPicker
              id="move-product"
              value={product}
              onPick={(p) => { setProduct(p); setColour(''); setSize(''); move.reset(); }}
            />
          </Field>
        </div>
        {product && (
          <OptionChoice
            product={product} colour={colour} size={size} idPrefix="move"
            setColour={(c) => { setColour(c); move.reset(); }}
            setSize={(s) => { setSize(s); move.reset(); }}
          />
        )}
      </div>
      {move.error && <Callout tone="danger" title={move.error.message} className="mt-3" />}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="submit"
          className={buttonClass('primary', 'sm')}
          disabled={!product || !colour || !size || same || move.isPending}
        >
          {move.isPending ? 'Moving it' : 'Move the stock item'}
        </button>
        <button type="button" className={buttonClass('secondary', 'sm')} onClick={() => { setOpen(false); move.reset(); }}>
          Cancel
        </button>
      </div>
      <p className="mt-3 text-[12px] text-muted">
        The item keeps its code, its stock and its history. If the new place holds an empty item
        that was never used, the empty one is removed.
      </p>
    </form>
  );
}

type Check = {
  rows: number; same: number; changed: number; moves: number; status_changes: number; replaces_empty: number;
  code_changes?: number; shopify_code_changes?: number; supplier_changes?: number; cost_changes?: number;
  undated?: number;
  stale?: Array<{ row: number; code: string | null; detail: string | null }>;
  unlisted: Array<{ product: string; colour: string; items: number }>;
  errors: Array<{ row: number; code: string | null; detail: string }>;
  changes: Array<{ row: number; code: string; action: string; detail: string | null; stale?: boolean }>;
};
type Applied = {
  moved: number; status_changed: number; replaced_empty: number;
  code_changed?: number; supplier_changed?: number; cost_changed?: number; skipped_stale?: number;
  emptied_products: Array<{ id: string; name: string; active: boolean }>;
};

/** The file as the server takes it: a CSV as text, an Excel workbook as base64 (FR-1436). */
type Upload = { csv: string } | { xlsx: string };

async function readFileForUpload(f: File): Promise<Upload> {
  if (/\.xlsx$/i.test(f.name)) {
    const bytes = new Uint8Array(await f.arrayBuffer());
    let bin = '';
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    return { xlsx: btoa(bin) };
  }
  return { csv: await f.text() };
}

const n = (x: number | undefined) => (x ?? 0).toLocaleString('en-AU');

/**
 * FR-1130 to FR-1136, and the bulk update of spec 014 (FR-1430 to FR-1436).
 * Download, fill in, check, apply.
 */
export function MatchingPanel({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<Upload | null>(null);
  const [fileName, setFileName] = useState('');
  // FR-1434. Off by default: a row changed since the download is skipped.
  const [anyway, setAnyway] = useState(false);
  const check = useMutation({
    mutationFn: async (up: Upload) =>
      ((await post('/api/stock/matching/check', up)) as { check: Check }).check,
  });
  const apply = useMutation({
    mutationFn: async () =>
      ((await post('/api/stock/matching/apply', { ...file, apply_anyway: anyway })) as { result: Applied }).result,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'stock'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'style'] });
      void qc.invalidateQueries({ queryKey: ['stock-item'] });
    },
  });
  const c = check.data;
  const stale = c?.stale ?? [];
  const willApply = c ? c.changed - (anyway ? 0 : stale.length) : 0;

  return (
    <section aria-label="Match stock to products" className="mb-4 rounded-[10px] border border-line bg-panel p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-[15px] font-semibold">Match and update stock items</p>
        <button type="button" className={buttonClass('tertiary', 'sm')} onClick={onClose}>Close</button>
      </div>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-[13px]">
        <li>
          Download the stock file and the product list.{' '}
          <span className="inline-flex flex-wrap gap-2 align-middle">
            <DownloadCsv path="/api/stock/matching.csv" params={{}} label="Stock file" />
            <DownloadCsv path="/api/products/matching.csv" params={{}} label="Product list" />
          </span>
        </li>
        <li>
          In Excel, change the columns you need: Parent product ID, Colour, Size, Class, New code,
          Main supplier and Average cost. Copy a product ID from the product list. An empty New
          code or Average cost keeps the value. Suggested product and Suggested colour are a guess
          from the old Shopify name.
        </li>
        <li>
          The file does not change a quantity. Stock comes in by a receipt, an adjustment or a
          stocktake.
        </li>
        <li>Make any missing product, colour, size or supplier first.</li>
        <li>
          Choose the file here, as .xlsx or as CSV, and check it. Nothing changes until you apply.
        </li>
      </ol>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          type="file"
          accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          aria-label="The matching file"
          className="text-[13px]"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            apply.reset();
            check.reset();
            setAnyway(false);
            if (!f) { setFile(null); return; }
            const up = await readFileForUpload(f);
            setFile(up);
            setFileName(f.name);
            check.mutate(up);
          }}
        />
        {check.isPending && <span className="text-[13px] text-muted">Checking every row</span>}
      </div>

      {check.error && <Callout tone="danger" title={check.error.message} className="mt-3" />}

      {c && !apply.data && (
        <div className="mt-3 text-[13px]">
          <p>
            {fileName}: {n(c.rows)} rows. {n(c.changed)} change, {n(c.same)} do not change.
          </p>
          {c.changed > 0 && (
            <ul className="mt-1 list-disc pl-5 text-muted">
              {c.moves > 0 && <li>{n(c.moves)} to another product, colour or size{c.replaces_empty > 0 && <>, {n(c.replaces_empty)} in place of an empty item</>}</li>}
              {c.status_changes > 0 && <li>{n(c.status_changes)} to another class</li>}
              {(c.code_changes ?? 0) > 0 && <li>{n(c.code_changes)} to a new code</li>}
              {(c.supplier_changes ?? 0) > 0 && <li>{n(c.supplier_changes)} to another main supplier</li>}
              {(c.cost_changes ?? 0) > 0 && <li>{n(c.cost_changes)} to a new average cost</li>}
            </ul>
          )}
          {c.errors.length > 0 ? (
            <>
              <Callout
                tone="danger"
                className="mt-2"
                title={`${c.errors.length} ${c.errors.length === 1 ? 'row has an error' : 'rows have errors'}. Fix them in the file and choose it again. Nothing has changed.`}
              />
              <ul className="mt-2 max-h-[260px] overflow-y-auto rounded-[8px] border border-line">
                {c.errors.map((e) => (
                  <li key={e.row} className="border-b border-line-soft px-3 py-1.5 last:border-b-0">
                    <span className="font-[family-name:var(--font-mono)]">Row {e.row}</span>
                    {e.code && <span className="text-muted"> {e.code}</span>}: {e.detail}
                  </li>
                ))}
              </ul>
            </>
          ) : c.changed === 0 ? (
            <p className="mt-2 text-muted">The file changes nothing.</p>
          ) : (
            <>
              <ul className="mt-2 max-h-[200px] overflow-y-auto rounded-[8px] border border-line">
                {c.changes.slice(0, 200).map((x) => (
                  <li key={x.row} className="border-b border-line-soft px-3 py-1.5 last:border-b-0">
                    <span className="font-[family-name:var(--font-mono)]">Row {x.row}</span> {x.code}: {x.detail ?? x.action}
                    {x.stale && <span className="font-semibold"> Changed since download.</span>}
                  </li>
                ))}
              </ul>
              {c.changes.length > 200 && <p className="mt-1 text-muted">And {c.changes.length - 200} more.</p>}
              {(c.shopify_code_changes ?? 0) > 0 && (
                <Callout
                  tone="warn"
                  className="mt-2"
                  title={`${n(c.shopify_code_changes)} of the new codes are on items that Shopify sells. The code is the link to Shopify, so change the SKU in Shopify to the same code.`}
                />
              )}
              {(c.undated ?? 0) > 0 && (
                <Callout
                  tone="warn"
                  className="mt-2"
                  title="This file has no Downloaded at column, so the check cannot see the rows that changed since the file was made. Download a new file to be safe."
                />
              )}
              {stale.length > 0 && (
                <Callout
                  tone="warn"
                  className="mt-2"
                  title={`${stale.length} ${stale.length === 1 ? 'row' : 'rows'} changed since download: somebody edited the item, or stock moved. Apply skips ${stale.length === 1 ? 'it' : 'them'}, so an old sheet does not overwrite a newer change.`}
                >
                  <label className="mt-1 flex items-center gap-2">
                    <input type="checkbox" checked={anyway} onChange={(e) => setAnyway(e.target.checked)} />
                    Apply anyway
                  </label>
                </Callout>
              )}
              {c.unlisted.length > 0 && (
                <Callout
                  tone="warn"
                  className="mt-2"
                  title="Shopify sells some of these items, and their new colour is not in the shop. Their next Shopify order will wait until you put the colour in the shop."
                >
                  <ul className="mt-1 list-disc pl-5">
                    {c.unlisted.map((u) => (
                      <li key={`${u.product}|${u.colour}`}>{u.product} in {u.colour}: {u.items} {u.items === 1 ? 'item' : 'items'}</li>
                    ))}
                  </ul>
                </Callout>
              )}
              {apply.error && <Callout tone="danger" title={apply.error.message} className="mt-2" />}
              <button
                type="button"
                className={`${buttonClass('primary', 'sm')} mt-3`}
                disabled={apply.isPending || willApply === 0}
                onClick={() => apply.mutate()}
              >
                {apply.isPending ? 'Applying' : `Apply ${willApply} ${willApply === 1 ? 'change' : 'changes'}`}
              </button>
            </>
          )}
        </div>
      )}

      {apply.data && (
        <div className="mt-3 text-[13px]">
          <Callout
            tone="ok"
            title={`Done. ${apply.data.moved} moved, ${apply.data.status_changed} classes, ${apply.data.code_changed ?? 0} codes, `
              + `${apply.data.supplier_changed ?? 0} main suppliers and ${apply.data.cost_changed ?? 0} costs changed.`
              + ((apply.data.skipped_stale ?? 0) > 0
                ? ` ${apply.data.skipped_stale} rows changed since download were skipped.` : '')}
          />
          {apply.data.emptied_products.length > 0 && (
            <>
              <p className="mt-2">
                These products have no stock items now. Set them inactive on their page if they are no longer used:
              </p>
              <ul className="mt-1 list-disc pl-5">
                {apply.data.emptied_products.map((p) => (
                  <li key={p.id}>
                    <a className="underline" href={`/admin/styles/${p.id}`}>{p.name}</a>
                    {!p.active && <span className="text-muted"> (inactive)</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}
